import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db, collection, addDoc, serverTimestamp, getDoc, doc, updateDoc } from '../config/firebase';
import { toast } from 'sonner';
import { User } from 'firebase/auth';
import { FieldValue, Timestamp } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  MapPin,
  Tag,
  Ticket,
  Image,
  Info,
  HelpCircle,
  ImagePlus,
  X,
  Loader2,
  Plus,
  AlertCircle,
  Globe,
  Lock,
  Link,
  Clock
} from 'lucide-react';
import { Progress } from '../components/ui/progress';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { SimpleMap } from '../components/Map/SimpleMap';
import { cn } from '../lib/utils';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { v4 as uuidv4 } from 'uuid';
import { imageService } from '../services/imageService';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "../components/ui/form";
import { Spinner } from '../components/ui/spinner';
import { EVENT_COVER_REQUIREMENTS } from '../config/imageRequirements';
import { auth, storage } from "../config/firebase";
import { EventMedia, Location as EventLocation, TicketTier as EventTicketTier } from '../types/events';

// Enable development mode to bypass Firebase permissions
const DEV_MODE = false; // Set to false in production

// Local storage key for events
const LOCAL_EVENTS_KEY = 'eventory_local_events';

// Form data types
interface FormData {
  title: string;
  description: string;
  category: string;
  tags?: string[];
  visibility: 'public' | 'private' | 'unlisted';
  dateTime: {
    startDate: Date;
    endDate?: Date;
    startTime: string;
    endTime?: string;
  };
  venue: string;
  location: EventLocation;
  tickets: {
    tiers: EventTicketTier[];
  };
  media: {
    imageUrl: string;
    altText?: string;
    images?: string[];
  };
}

// Event data interface for Firestore (matches src/types/events.ts:EventData)
interface EventDataForFirestore {
  id?: string;
  title: string;
  description: string;
  category: string;
  visibility: 'public' | 'private' | 'unlisted';
  startDate: Timestamp;
  endDate: Timestamp | null;
  startTime: string;
  endTime: string | null;
  venue: string;
  location: EventLocation;
  coverImage: string;
  media?: EventMedia;
  ticketTiers: EventTicketTier[];
  organizerId: string;
  organizerName: string;
  createdAt: FieldValue;
  updatedAt: FieldValue;
  status: 'draft' | 'published' | 'cancelled' | 'pending_review';
}

interface StepProps {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  onNext: (data: Partial<FormData>) => void;
  onPrev: () => void;
  isLoading: boolean;
  isFirst: boolean;
  isLast: boolean;
}

// Step validation schemas
const basicInfoSchema = z.object({
  title: z.string().min(3, 'Title must be at least 3 characters'),
  description: z.string().min(10, 'Description must be at least 10 characters'),
  category: z.string().min(1, 'Please select a category'),
  tags: z.array(z.string()).optional(),
  visibility: z.enum(['public', 'private', 'unlisted']),
});

const dateTimeSchema = z.object({
  startDate: z.date({ required_error: 'Start date is required' }),
  endDate: z.date().optional(),
  startTime: z.string().min(1, 'Start time is required'),
  endTime: z.string().optional(),
});

const locationSchema = z.object({
  venue: z.string().min(1, "Venue is required"),
  address: z.string().min(1, "Address is required"),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  postcode: z.string().min(1, "Postcode is required"),
  coordinates: z.object({
    lat: z.number().min(0, 'Latitude must be 0 or greater'),
    lng: z.number().min(0, 'Longitude must be 0 or greater'),
  }).optional(),
});

const ticketSchema = z.object({
  tiers: z.array(z.object({
    name: z.string().min(1, 'Tier name is required'),
    price: z.number().min(0, 'Price must be 0 or greater'),
    quantity: z.number().min(1, 'Quantity must be at least 1'),
    description: z.string(),
    sold: z.number().default(0),
  })).min(1, 'At least one ticket tier is required'),
});

const mediaSchema = z.object({
  imageUrl: z.string().url('Please enter a valid image URL'),
  altText: z.string().optional(),
  images: z.array(z.string().url()).optional(), // Ensure images is an array of URL strings
});

const steps = [
  { id: 'basic-info', title: 'Basic Information', icon: Info },
  { id: 'date-time', title: 'Date & Time', icon: CalendarIcon },
  { id: 'location', title: 'Location', icon: MapPin },
  { id: 'tickets', title: 'Tickets', icon: Ticket },
  { id: 'media', title: 'Media', icon: Image },
];

const categories = [
  'Music', 'Technology', 'Business', 'Arts', 'Sports', 'Food', 'Education', 'Other'
];

interface CreateEventProps {
  initialData?: EventDataForFirestore; // Use the specific Firestore interface
  isEditing?: boolean;
}

const CreateEvent: React.FC<CreateEventProps> = ({ initialData, isEditing = false }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [permissionLoading, setPermissionLoading] = useState(true);
  const [permissionError, setPermissionError] = useState<string | null>(null);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user && !permissionLoading) {
      toast.error('Please log in to create events');
      navigate('/login');
    }
  }, [user, permissionLoading, navigate]);

  // Initialize form data
  const [formData, setFormData] = useState<FormData>(() => {
    if (initialData) {
      return {
        title: initialData.title,
        description: initialData.description,
        category: initialData.category,
        visibility: initialData.visibility,
        dateTime: {
          startDate: initialData.startDate.toDate(), // Convert Timestamp to Date
          endDate: initialData.endDate?.toDate() || undefined, // Convert Timestamp to Date
          startTime: initialData.startTime,
          endTime: initialData.endTime || undefined,
        },
        venue: initialData.venue,
        location: initialData.location,
        tickets: {
          tiers: initialData.ticketTiers || [],
        },
        media: {
          imageUrl: initialData.coverImage,
          altText: '',
          images: initialData.media?.images || [],
        },
      };
    }
    return {
      title: '',
      description: '',
      category: '',
      visibility: 'public',
      dateTime: {
        startDate: new Date(),
        startTime: '',
      },
      venue: '',
      location: {
        address: '',
        city: '',
        state: '',
        postcode: '',
        coordinates: { lat: 25.2048, lng: 55.2708 }, // Default to Dubai
      },
      tickets: {
        tiers: [],
      },
      media: {
        imageUrl: '',
        images: [],
      },
    };
  });

  // Check permissions after user is authenticated
  useEffect(() => {
  const checkPermissions = async () => {
    if (!user) return;

    setPermissionLoading(true);
    setPermissionError(null);

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();

      if (!userData?.roles?.includes('organizer')) {
        try {
          await updateDoc(doc(db, 'users', user.uid), {
            roles: [...(userData?.roles || []), 'organizer']
          });
          toast.success('You have been granted organizer permissions');
        } catch (error: unknown) {
          console.error('Error granting organizer role:', error);
          setPermissionError('Unable to grant organizer permissions. Please contact support.');
          navigate('/');
          return;
        }
      }
    } catch (error: unknown) {
      console.error('Error checking permissions:', error);
      setPermissionError('Error checking permissions. Please try again later.');
    } finally {
      setPermissionLoading(false);
    }
  };

    checkPermissions();
  }, [user, navigate]);

  const handleNext = (stepData: Partial<FormData>) => {
    setFormData(prev => ({ ...prev, ...stepData }));

    if (currentStep === steps.length - 1) {
      handleSubmit();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!user) {
      toast.error('You must be logged in to create an event');
      return;
    }

    setIsLoading(true);
    try {
      toast.loading('Creating your event...', {
        duration: Infinity,
        id: 'event-creation'
      });

      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }

      const userData = userDoc.data();
      if (!userData?.roles?.includes('organizer')) {
        throw new Error('You do not have permission to create events');
      }

      const requiredFields = {
        title: formData.title,
        description: formData.description,
        startDate: formData.dateTime.startDate,
        startTime: formData.dateTime.startTime,
        venue: formData.venue,
        location: formData.location.address // Check nested property
      };

      const missingFields = Object.entries(requiredFields)
        .filter(([key, value]) => {
          // Special handling for location.address if location object exists
          if (key === 'location' && typeof value === 'object' && value !== null) {
            return !value.address; // Check address property within location
          }
          return !value;
        })
        .map(([key]) => key === 'location' ? 'location address' : key); // More descriptive message

      if (missingFields.length > 0) {
        toast.dismiss('event-creation');
        toast.error(`Please fill in all required fields: ${missingFields.join(', ')}`);
        setIsLoading(false);
        return;
      }

      const startDateTime = Timestamp.fromDate(new Date(
        formData.dateTime.startDate.getFullYear(),
        formData.dateTime.startDate.getMonth(),
        formData.dateTime.startDate.getDate(),
        parseInt(formData.dateTime.startTime.split(':')[0]),
        parseInt(formData.dateTime.startTime.split(':')[1])
      ));

      let endDateTime: Timestamp | null = null;
      if (formData.dateTime.endDate && formData.dateTime.endTime) {
        endDateTime = Timestamp.fromDate(new Date(
          formData.dateTime.endDate.getFullYear(),
          formData.dateTime.endDate.getMonth(),
          formData.dateTime.endDate.getDate(),
          parseInt(formData.dateTime.endTime.split(':')[0]),
          parseInt(formData.dateTime.endTime.split(':')[1])
        ));

        if (endDateTime.seconds <= startDateTime.seconds) {
          toast.dismiss('event-creation');
          toast.error('End date must be after start date');
          setIsLoading(false);
          return;
        }
      }

      const status = userData.roles.includes('admin') ? 'published' : 'pending_review';

      const eventData: EventDataForFirestore = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
        visibility: formData.visibility,
        startDate: startDateTime,
        endDate: endDateTime,
        startTime: formData.dateTime.startTime,
        endTime: formData.dateTime.endTime || null,
        venue: formData.venue.trim(),
        location: formData.location,
        coverImage: formData.media.imageUrl,
        media: {
          images: formData.media.images || [],
        },
        ticketTiers: formData.tickets.tiers.map(tier => ({
          ...tier,
          price: Number(tier.price),
          quantity: Number(tier.quantity),
          sold: 0
        })),
        organizerId: user.uid,
        organizerName: user.displayName || 'Anonymous',
        status,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      try {
        const eventRef = await addDoc(collection(db, 'events'), eventData);

        toast.dismiss('event-creation');

        if (status === 'pending_review') {
          toast.success('Event created successfully!', {
            description: 'Your event will be published after admin review.',
            action: {
              label: 'View Event',
              onClick: () => navigate(`/events/${eventRef.id}`)
            }
          });
        } else {
          toast.success('Event created and published successfully!', {
            description: 'You will be redirected to your dashboard.',
            action: {
              label: 'View Event',
              onClick: () => navigate(`/events/${eventRef.id}`)
            }
          });
        }

        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } catch (error: unknown) {
        toast.dismiss('event-creation');
        if (error instanceof Error) {
          if (error.message.includes('permission')) {
            toast.error('Permission denied', {
              description: 'You do not have permission to create events. Please contact support.'
            });
          } else if (error.message.includes('validation')) {
            toast.error('Validation Error', {
              description: error.message
            });
          } else {
            toast.error('Failed to create event', {
              description: 'Please try again or contact support if the problem persists.'
            });
          }
        }
        console.error('Error creating event:', error);
      }
    } catch (error: unknown) {
      toast.dismiss('event-creation');
      toast.error('An unexpected error occurred', {
        description: 'Please try again or contact support if the problem persists.'
      });
      console.error('Form submission error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (permissionLoading) {
  return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="mt-4 text-lg">Checking permissions...</p>
                        </div>
                      </div>
    );
  }

  if (permissionError) {
  return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Permission Error</h2>
          <p className="text-muted-foreground mb-6">{permissionError}</p>
          <Button onClick={() => navigate('/')}>
            Return to Home
                      </Button>
                    </div>
          </div>
  );
}

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold mb-8">{isEditing ? 'Edit Event' : 'Create Event'}</h1>
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <p className="mt-2 text-gray-600">Create a new event in just a few steps</p>
          </div>

          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex justify-between items-center">
              {steps.map((step, index) => (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        index <= currentStep ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      <step.icon className="w-5 h-5" />
                    </div>
                    <span className="mt-2 text-sm text-gray-600">{step.title}</span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="flex-1 h-0.5 bg-gray-200 mx-4" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <Card>
            <CardHeader>
              <CardTitle>{steps[currentStep].title}</CardTitle>
            </CardHeader>
            <CardContent>
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  {currentStep === 0 && (
                    <BasicInfoStep
                      onNext={handleNext}
                      onPrev={handlePrev}
                      isFirst={true}
                      isLast={false}
                      formData={formData}
                      setFormData={setFormData}
                      isLoading={isLoading}
                    />
                  )}
                  {currentStep === 1 && (
                    <DateTimeStep
                      onNext={handleNext}
                      onPrev={handlePrev}
                      isFirst={false}
                      isLast={false}
                      formData={formData}
                      setFormData={setFormData}
                      isLoading={isLoading}
                    />
                  )}
                  {currentStep === 2 && (
                    <LocationStep
                      onNext={handleNext}
                      onPrev={handlePrev}
                      isFirst={false}
                      isLast={false}
                      formData={formData}
                      setFormData={setFormData}
                      isLoading={isLoading}
                    />
                  )}
                  {currentStep === 3 && (
                    <TicketsStep
                      onNext={handleNext}
                      onPrev={handlePrev}
                      isFirst={false}
                      isLast={false}
                      formData={formData}
                      setFormData={setFormData}
                      isLoading={isLoading}
                    />
                  )}
                  {currentStep === 4 && (
                    <MediaStep
                      onNext={handleNext}
                      onPrev={handlePrev}
                      isFirst={false}
                      isLast={true}
                      formData={formData}
                      setFormData={setFormData}
                      isLoading={isLoading}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Step Components
function BasicInfoStep({ onNext, onPrev, isFirst, isLast, formData, setFormData, isLoading }: StepProps) {
  const { handleSubmit, control, formState: { errors } } = useForm<z.infer<typeof basicInfoSchema>>({
    resolver: zodResolver(basicInfoSchema),
    defaultValues: {
      title: formData.title || '',
      description: formData.description || '',
      category: formData.category || '',
      tags: formData.tags || [],
      visibility: formData.visibility,
    },
  });

  const onSubmit: SubmitHandler<z.infer<typeof basicInfoSchema>> = (data) => {
    onNext(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <Alert className="mb-6">
        <Info className="h-4 w-4" />
        <AlertTitle>Tips for creating a great event</AlertTitle>
        <AlertDescription>
          Make your event title catchy and descriptive. Add details that will help attendees understand what to expect.
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        <div>
          <div className="flex items-center gap-2">
            <Label htmlFor="title">Event Title</Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Choose a clear, engaging title that describes your event</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                id="title"
                placeholder="Enter event title"
                className={cn(errors.title && "border-red-500")}
                disabled={isLoading}
              />
            )}
          />
          {errors.title && (
            <span className="text-sm text-red-500 mt-1">{errors.title.message}</span>
          )}
        </div>

        <div>
          <div className="flex items-center gap-2">
            <Label htmlFor="description">Description</Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Provide detailed information about your event</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <Textarea
                {...field}
                id="description"
                placeholder="Describe your event"
                rows={4}
                className={cn(errors.description && "border-red-500")}
                disabled={isLoading}
              />
            )}
          />
          {errors.description && (
            <span className="text-sm text-red-500 mt-1">{errors.description.message}</span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="category">Category</Label>
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <SelectTrigger className={cn(errors.category && "border-red-500")}>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category} value={category.toLowerCase()}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.category && (
              <span className="text-sm text-red-500 mt-1">{errors.category.message}</span>
            )}
          </div>

        </div>

        <div>
          <Label>Visibility</Label>
          <Controller
            name="visibility"
            control={control}
            render={({ field }) => (
              <RadioGroup
                onValueChange={field.onChange}
                defaultValue={field.value}
                className="grid grid-cols-3 gap-4 mt-2"
              >
                <div>
                  <RadioGroupItem
                    value="public"
                    id="public"
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor="public"
                    className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-transparent p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                  >
                    <Globe className="mb-2 h-6 w-6" />
                    Public
                  </Label>
                </div>
                <div>
                  <RadioGroupItem
                    value="private"
                    id="private"
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor="private"
                    className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-transparent p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                  >
                    <Lock className="mb-2 h-6 w-6" />
                    Private
                  </Label>
                </div>
                <div>
                  <RadioGroupItem
                    value="unlisted"
                    id="unlisted"
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor="unlisted"
                    className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-transparent p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                  >
                    <Link className="mb-2 h-6 w-6" />
                    Unlisted
                  </Label>
                </div>
              </RadioGroup>
            )}
          />
        </div>
      </div>

      <Separator className="my-6" />

      <div className="flex justify-between pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onPrev}
          disabled={isFirst || isLoading}
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </form>
  );
}

function DateTimeStep({ onNext, onPrev, isFirst, isLast, formData, setFormData, isLoading }: StepProps) {
  const { handleSubmit, control, formState: { errors }, watch } = useForm<z.infer<typeof dateTimeSchema>>({
    resolver: zodResolver(dateTimeSchema),
    defaultValues: {
      startDate: formData.dateTime.startDate || new Date(),
      endDate: formData.dateTime.endDate,
      startTime: formData.dateTime.startTime || '',
      endTime: formData.dateTime.endTime || '',
    },
  });

  const startDate = watch('startDate');

  const onSubmit: SubmitHandler<z.infer<typeof dateTimeSchema>> = (data) => {
    onNext({ dateTime: data });
  };

  return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      <div className="rounded-xl border bg-gradient-to-b from-white to-gray-50/50 shadow-sm">
        <div className="flex flex-row items-center gap-6 p-8 border-b">
          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <CalendarIcon className="h-6 w-6 text-primary" />
          </div>
          <div className="space-y-1.5">
            <h3 className="text-xl font-semibold tracking-tight">Schedule Your Event</h3>
            <p className="text-sm text-muted-foreground">
              Choose when your event starts and ends. For multi-day events, you can specify an end date.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <div className="space-y-6">
          <div className="space-y-3">
            <Label className="text-sm font-medium">Start Date</Label>
            <Controller
              name="startDate"
              control={control}
              render={({ field }) => (
                  <Popover>
                    <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                        "w-full justify-start text-left font-normal h-[52px] px-4",
                        "bg-white border-2 transition-all duration-200",
                        "hover:border-primary/50 hover:bg-primary/5",
                        !field.value && "text-muted-foreground",
                        errors.startDate && "border-destructive/50 hover:border-destructive",
                        field.value && "border-primary/30"
                      )}
                    >
                      <CalendarIcon className="mr-3 h-5 w-5 text-primary/60" />
                          {field.value ? (
                        <span className="font-medium">{format(field.value, "EEEE, MMMM d, yyyy")}</span>
                          ) : (
                        <span>Select start date</span>
                          )}
                        </Button>
                    </PopoverTrigger>
                  <PopoverContent 
                    className="w-auto p-0" 
                    align="start"
                  >
                    <div className="p-4 border-b">
                      <h4 className="font-medium">Select Date</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Choose the start date for your event
                      </p>
                    </div>
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                      disabled={(date) =>
                        date < new Date(new Date().setHours(0, 0, 0, 0))
                      }
                        initialFocus
                      className="p-4"
                      classNames={{
                        months: "flex flex-col space-y-4",
                        month: "space-y-4",
                        caption: "relative flex items-center justify-center",
                        caption_label: "text-sm font-medium",
                        nav: "flex items-center space-x-1 bg-white rounded-lg p-1",
                        nav_button_previous: "absolute left-1 bg-white rounded-lg hover:bg-gray-100 p-1",
                        nav_button_next: "absolute right-1 bg-white rounded-lg hover:bg-gray-100 p-1",
                        table: "w-full border-collapse",
                        head_row: "flex w-full",
                        head_cell: "w-[42px] font-medium text-xs text-muted-foreground rounded-md m-0.5 text-center",
                        row: "flex w-full mt-2",
                        cell: cn(
                          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent rounded-md m-0.5",
                          "first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
                        ),
                        day: cn(
                          "h-[42px] w-[42px] p-0 font-normal text-sm rounded-md",
                          "aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                        ),
                        day_selected: cn(
                          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                          "before:absolute before:h-1 before:w-1 before:rounded-full before:bottom-1 before:left-1/2 before:-translate-x-1/2 before:bg-white"
                        ),
                        day_today: cn(
                          "bg-accent/50 text-accent-foreground",
                          "before:absolute before:h-1 before:w-1 before:rounded-full before:bottom-1 before:left-1/2 before:-translate-x-1/2 before:bg-primary"
                        ),
                        day_outside: "text-muted-foreground/50 opacity-50",
                        day_disabled: "text-muted-foreground/50 opacity-50 cursor-not-allowed",
                        day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
                        day_hidden: "invisible",
                      }}
                    />
                  </PopoverContent>
                </Popover>
              )}
            />
            {errors.startDate && (
              <span className="text-sm text-destructive flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                {errors.startDate.message}
              </span>
            )}
          </div>

          <div className="space-y-3">
            <Label htmlFor="startTime" className="text-sm font-medium">Start Time</Label>
            <Controller
              name="startTime"
              control={control}
              render={({ field }) => (
                <div className="relative">
                        <Input
                    {...field}
                    id="startTime"
                          type="time"
                    className={cn(
                      "h-[52px] pl-11",
                      "bg-white border-2 transition-all duration-200",
                      "hover:border-primary/50",
                      errors.startTime && "border-destructive/50 hover:border-destructive",
                      field.value && "border-primary/30"
                    )}
                    disabled={isLoading}
                  />
                  <div className="absolute left-4 top-1/2 -translate-y-1/2">
                    <Clock className="h-5 w-5 text-primary/60" />
                      </div>
                </div>
              )}
            />
            {errors.startTime && (
              <span className="text-sm text-destructive flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                {errors.startTime.message}
              </span>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="space-y-3">
            <Label className="text-sm font-medium">End Date (Optional)</Label>
            <Controller
              name="endDate"
              control={control}
              render={({ field }) => (
                  <Popover>
                    <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                        "w-full justify-start text-left font-normal h-[52px] px-4",
                        "bg-white border-2 transition-all duration-200",
                        "hover:border-primary/50 hover:bg-primary/5",
                        !field.value && "text-muted-foreground",
                        errors.endDate && "border-destructive/50 hover:border-destructive",
                        field.value && "border-primary/30"
                      )}
                    >
                      <CalendarIcon className="mr-3 h-5 w-5 text-primary/60" />
                          {field.value ? (
                        <span className="font-medium">{format(field.value, "EEEE, MMMM d, yyyy")}</span>
                          ) : (
                        <span>Select end date</span>
                          )}
                        </Button>
                    </PopoverTrigger>
                  <PopoverContent 
                    className="w-auto p-0" 
                    align="start"
                  >
                    <div className="p-4 border-b">
                      <h4 className="font-medium">Select End Date</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Choose when your event ends
                      </p>
                    </div>
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                      disabled={(date) =>
                        date < startDate || date < new Date(new Date().setHours(0, 0, 0, 0))
                      }
                        initialFocus
                      className="p-4"
                      classNames={{
                        months: "flex flex-col space-y-4",
                        month: "space-y-4",
                        caption: "relative flex items-center justify-center",
                        caption_label: "text-sm font-medium",
                        nav: "flex items-center space-x-1 bg-white rounded-lg p-1",
                        nav_button_previous: "absolute left-1 bg-white rounded-lg hover:bg-gray-100 p-1",
                        nav_button_next: "absolute right-1 bg-white rounded-lg hover:bg-gray-100 p-1",
                        table: "w-full border-collapse",
                        head_row: "flex w-full",
                        head_cell: "w-[42px] font-medium text-xs text-muted-foreground rounded-md m-0.5 text-center",
                        row: "flex w-full mt-2",
                        cell: cn(
                          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent rounded-md m-0.5",
                          "first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
                        ),
                        day: cn(
                          "h-[42px] w-[42px] p-0 font-normal text-sm rounded-md",
                          "aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                        ),
                        day_selected: cn(
                          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                          "before:absolute before:h-1 before:w-1 before:rounded-full before:bottom-1 before:left-1/2 before:-translate-x-1/2 before:bg-white"
                        ),
                        day_today: cn(
                          "bg-accent/50 text-accent-foreground",
                          "before:absolute before:h-1 before:w-1 before:rounded-full before:bottom-1 before:left-1/2 before:-translate-x-1/2 before:bg-primary"
                        ),
                        day_outside: "text-muted-foreground/50 opacity-50",
                        day_disabled: "text-muted-foreground/50 opacity-50 cursor-not-allowed",
                        day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
                        day_hidden: "invisible",
                      }}
                    />
                  </PopoverContent>
                </Popover>
              )}
            />
            {errors.endDate && (
              <span className="text-sm text-destructive flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                {errors.endDate.message}
              </span>
            )}
          </div>

          <div className="space-y-3">
            <Label htmlFor="endTime" className="text-sm font-medium">End Time (Optional)</Label>
            <Controller
              name="endTime"
              control={control}
              render={({ field }) => (
                <div className="relative">
                        <Input
                    {...field}
                    id="endTime"
                          type="time"
                    className={cn(
                      "h-[52px] pl-11",
                      "bg-white border-2 transition-all duration-200",
                      "hover:border-primary/50",
                      errors.endTime && "border-destructive/50 hover:border-destructive",
                      field.value && "border-primary/30"
                    )}
                    disabled={isLoading}
                  />
                  <div className="absolute left-4 top-1/2 -translate-y-1/2">
                    <Clock className="h-5 w-5 text-primary/60" />
                      </div>
                </div>
              )}
            />
            {errors.endTime && (
              <span className="text-sm text-destructive flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                {errors.endTime.message}
              </span>
            )}
          </div>
          </div>

      </div>

      <Separator className="my-8" />

      <div className="flex justify-between pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onPrev}
          disabled={isFirst || isLoading}
          className="h-11 px-6"
        >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
        <Button 
          type="submit" 
          disabled={isFirst || isLoading}
          className="h-11 px-6 bg-primary hover:bg-primary/90"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </>
          )}
            </Button>
          </div>
        </form>
  );
}

function LocationStep({ onNext, onPrev, isFirst, isLast, formData, setFormData, isLoading }: StepProps) {
  const form = useForm<z.infer<typeof locationSchema>>({
    resolver: zodResolver(locationSchema),
    defaultValues: {
      venue: formData.venue || '',
      address: formData.location?.address || '',
      city: formData.location?.city || '',
      state: formData.location?.state || '',
      postcode: formData.location?.postcode || '',
      coordinates: formData.location?.coordinates || { lat: 25.2048, lng: 55.2708 },
    },
  });

  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  // Debounced search
  const searchAddress = (query: string) => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    if (abortControllerRef.current) abortControllerRef.current.abort();
    if (!query) {
      setSearchResults([]);
      setSearchError(null);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    setSearchError(null);
    debounceTimeout.current = setTimeout(async () => {
      try {
        abortControllerRef.current = new AbortController();
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`,
          { signal: abortControllerRef.current.signal }
        );
        if (!response.ok) throw new Error('Failed to fetch');
        const data = await response.json();
        setSearchResults(data);
        setSearchError(data.length === 0 ? 'No results found.' : null);
      } catch (error: unknown) {
        if (error instanceof Error && error.name !== 'AbortError') {
          setSearchError('Error searching address');
          setSearchResults([]);
        }
      } finally {
        setIsSearching(false);
      }
    }, 300);
  };

  // Keyboard navigation for suggestions
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (searchResults.length === 0) return;
    if (e.key === 'ArrowDown') {
      setHighlightedIndex(i => Math.min(i + 1, searchResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      setHighlightedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      handleAddressSelect(searchResults[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setSearchResults([]);
      setHighlightedIndex(-1);
    }
  };

  const handleAddressSelect = (result: any) => {
    const coordinates = {
      lat: parseFloat(result.lat),
      lng: parseFloat(result.lon)
    };
    form.setValue('address', result.display_name);
    form.setValue('coordinates', coordinates);
    setSearchResults([]);
    setHighlightedIndex(-1);
    // Try to extract city, state, and postcode from address components
    const addressParts = result.display_name.split(', ');
    // Fallback: allow manual override
    form.setValue('city', form.getValues('city') || '');
    form.setValue('state', form.getValues('state') || '');
    form.setValue('postcode', form.getValues('postcode') || '');
    // Try to parse city/state/postcode from addressParts
    addressParts.forEach((part: string) => {
      if (!form.getValues('city') && /city|town|municipality/i.test(part)) form.setValue('city', part.replace(/(city|town|municipality)/i, '').trim());
      if (!form.getValues('state') && /state|province|region/i.test(part)) form.setValue('state', part.replace(/(state|province|region)/i, '').trim());
      if (!form.getValues('postcode') && /\d{5,}/.test(part)) form.setValue('postcode', part.trim());
    });
  };

  function onSubmit(data: z.infer<typeof locationSchema>) {
    const locationData = {
      venue: data.venue,
      location: {
        address: data.address,
        city: data.city,
        state: data.state,
        postcode: data.postcode,
        coordinates: data.coordinates
      }
    };
    setFormData(prev => ({ ...prev, ...locationData }));
    onNext(locationData);
  }

  // Map preview
  const coordinates = form.watch('coordinates');

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="venue"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Venue Name</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Enter venue name" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem className="relative">
                <FormLabel>Location</FormLabel>
                <div className="relative">
                <FormControl>
                    <Input
                      {...field}
                      placeholder="Search for a location..."
                      onChange={e => {
                        field.onChange(e);
                        searchAddress(e.target.value);
                      }}
                      onKeyDown={handleKeyDown}
                      autoComplete="off"
                  />
                </FormControl>
                  {isSearching && (
                    <div className="absolute right-3 top-3">
                      <Spinner className="h-4 w-4" />
                    </div>
                  )}
                </div>
                {searchResults.length > 0 && (
                  <Card className="absolute z-50 mt-1 max-h-60 w-full overflow-auto">
                    <ul className="divide-y divide-border">
                      {searchResults.map((result, idx) => (
                        <li
                          key={result.place_id}
                          className={cn(
                            'cursor-pointer p-2 hover:bg-accent',
                            idx === highlightedIndex && 'bg-primary/10'
                          )}
                          onClick={() => handleAddressSelect(result)}
                          onMouseEnter={() => setHighlightedIndex(idx)}
                        >
                          {result.display_name}
                        </li>
                      ))}
                    </ul>
                  </Card>
                )}
                {searchError && !isSearching && (
                  <div className="text-xs text-red-500 mt-1">{searchError}</div>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>City</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="City" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="state"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>State/Province</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="State/Province" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

            <FormField
            control={form.control}
              name="postcode"
              render={({ field }) => (
                <FormItem>
                <FormLabel>Postal Code</FormLabel>
                  <FormControl>
                  <Input {...field} placeholder="Postal Code" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

        {/* Map Preview */}
        <div className="my-4">
          {coordinates && coordinates.lat && coordinates.lng && (
            <div className="rounded-xl overflow-hidden border border-slate-200 bg-white shadow-sm">
              <div className="p-2 text-xs text-gray-500">Map Preview</div>
              <div style={{ height: 240 }}>
                {/** Memoize center for SimpleMap */}
                {(() => {
                  const mapCenter = useMemo(() => ({ lat: coordinates.lat, lng: coordinates.lng }), [coordinates.lat, coordinates.lng]);
                  return (
                    <SimpleMap
                      center={mapCenter}
                      zoom={15}
                      markers={[{ position: mapCenter }]}
                      height="240px"
                    />
                  );
                })()}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={onPrev}
            disabled={isFirst || isLoading}
          >
              Previous
            </Button>
            <Button type="submit" disabled={isLoading}>
            {isLast ? "Create Event" : "Next"}
            </Button>
          </div>
        </form>
    </Form>
  );
}

function TicketsStep({ onNext, onPrev, isFirst, isLast, formData, setFormData, isLoading }: StepProps) {
  const [tiers, setTiers] = useState<EventTicketTier[]>(formData.tickets?.tiers || []);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handlePriceChange = (index: number, value: string) => {
    const newTiers = [...tiers];
    const numValue = value === '' ? 0 : Number(value);
    newTiers[index].price = isNaN(numValue) ? 0 : numValue;
    setTiers(newTiers);
  };

  const handleQuantityChange = (index: number, value: string) => {
    const newTiers = [...tiers];
    const numValue = value === '' ? 1 : Number(value);
    newTiers[index].quantity = isNaN(numValue) ? 1 : Math.max(1, numValue);
    setTiers(newTiers);
  };

  const addTier = () => {
    setTiers([...tiers, {
      id: uuidv4(),
      name: '',
      price: 0,
      quantity: 1,
      description: '',
      sold: 0
    }]);
  };

  const removeTier = (index: number) => {
    if (tiers.length > 1) {
      setTiers(tiers.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const invalidTiers = tiers.filter(tier => !tier.name);
    if (invalidTiers.length > 0) {
      toast.error('Please provide names for all ticket tiers');
      return;
    }

    const negativePrices = tiers.filter(tier => isNaN(tier.price) || tier.price < 0);
    if (negativePrices.length > 0) {
      toast.error('Ticket prices cannot be negative');
      return;
    }

    const invalidQuantities = tiers.filter(tier => isNaN(tier.quantity) || tier.quantity < 1);
    if (invalidQuantities.length > 0) {
      toast.error('Ticket quantities must be at least 1');
      return;
    }

    const tiersWithIds = tiers.map(tier => ({
      ...tier,
      id: tier.id || uuidv4(),
      sold: tier.sold || 0
    }));

    const ticketsData = {
      tickets: {
        tiers: tiersWithIds.map(tier => ({
          ...tier,
          price: Number(tier.price),
          quantity: Number(tier.quantity),
          sold: tier.sold
        }))
      }
    };

    setFormData(prev => ({
      ...prev,
      tickets: {
        tiers: ticketsData.tickets.tiers
      }
    }));

    onNext(ticketsData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Alert className="mb-6">
        <Ticket className="h-4 w-4" />
        <AlertTitle>Ticket Information</AlertTitle>
        <AlertDescription>
          Create different ticket tiers with varying prices and quantities.
        </AlertDescription>
      </Alert>

      {tiers.map((tier, index) => (
        <div key={index} className="p-4 border rounded-lg space-y-4">
          <div className="flex justify-between items-center">
            <h4 className="font-medium">Ticket Tier {index + 1}</h4>
            {tiers.length > 1 && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => removeTier(index)}
                disabled={isLoading}
              >
                <X className="h-4 w-4 mr-1" />
                Remove
              </Button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor={`tier-${index}-name`}>Name</Label>
                        <Input
                id={`tier-${index}-name`}
                          value={tier.name}
                          onChange={(e) => {
                  const newTiers = [...tiers];
                            newTiers[index].name = e.target.value;
                  setTiers(newTiers);
                          }}
                placeholder="e.g., Early Bird, VIP"
                disabled={isLoading}
                        />
            </div>
            <div>
              <Label htmlFor={`tier-${index}-price`}>Price</Label>
                          <Input
                id={`tier-${index}-price`}
                            type="number"
                min="0"
                step="0.01"
                value={tier.price.toString()}
                onChange={(e) => handlePriceChange(index, e.target.value)}
                disabled={isLoading}
              />
            </div>
          </div>

          <div>
            <Label htmlFor={`tier-${index}-quantity`}>Quantity Available</Label>
                          <Input
              id={`tier-${index}-quantity`}
                            type="number"
              min="1"
              value={tier.quantity.toString()}
              onChange={(e) => handleQuantityChange(index, e.target.value)}
              disabled={isLoading}
                          />
                        </div>

          <div>
            <Label htmlFor={`tier-${index}-description`}>Description</Label>
                        <Textarea
              id={`tier-${index}-description`}
                          value={tier.description}
                          onChange={(e) => {
                const newTiers = [...tiers];
                            newTiers[index].description = e.target.value;
                setTiers(newTiers);
                          }}
              placeholder="What's included in this ticket tier?"
              disabled={isLoading}
                        />
                      </div>
        </div>
      ))}

                      <Button
                        type="button"
        variant="outline"
        onClick={addTier}
        className="w-full"
        disabled={isLoading}
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Another Ticket Tier
                      </Button>

      <Separator className="my-6" />

      <div className="flex justify-between pt-4">
          <Button
            type="button"
            variant="outline"
          onClick={onPrev}
          disabled={isFirst || isLoading}
        >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
            <Button type="submit" disabled={isLoading}>
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </form>
  );
}

function MediaStep({ onNext, onPrev, isFirst, isLast, formData, setFormData, isLoading }: StepProps) {
  const [images, setImages] = useState<{ file: File; preview: string; url?: string; deleteUrl?: string }[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Initialize images from formData if available and not already set
    if (formData.media?.images && formData.media.images.length > 0 && images.length === 0) {
      setImages(formData.media.images.map(url => ({
        file: new File([], "existing-image.jpg", { type: "image/jpeg" }),
        preview: url,
        url: url,
      })));
    } else if (formData.media?.imageUrl && images.length === 0 && (!formData.media.images || formData.media.images.length === 0)) {
      // Fallback for legacy imageUrl if no images array exists
      setImages([{
        file: new File([], "existing-image.jpg", { type: "image/jpeg" }),
        preview: formData.media.imageUrl,
        url: formData.media.imageUrl
      }]);
    }

    // Cleanup function to revoke object URLs
    return () => {
      images.forEach(image => {
        if (image.preview && !image.preview.startsWith('http')) {
          URL.revokeObjectURL(image.preview);
        }
      });
    };
  }, [formData.media?.imageUrl, formData.media?.images]); // Add formData.media?.images to dependencies

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    setImages(prev => [...prev, ...newImages]);
    setIsUploading(true);
    setError(null);

    try {
      const totalFiles = newImages.length;
      const uploadedImages: Array<{ file: File; preview: string; url: string; deleteUrl?: string }> = [];

      for (let i = 0; i < totalFiles; i++) {
        const image = newImages[i];
        setUploadProgress(Math.round(((i + 1) / totalFiles) * 100)); // Update progress more accurately

        const result = await imageService.uploadImage(
          image.file,
          'events/covers',
          true
        );

        if (!result.url) {
          throw new Error('Failed to get upload URL from ImgBB');
        }

        uploadedImages.push({
          ...image,
          url: result.url,
          deleteUrl: result.deleteUrl
        });
      }

      setImages(prev => {
        const existingImages = prev.filter(img => img.url && img.url.startsWith('http'));
        return [...existingImages, ...uploadedImages];
      });

      // Update form data with the first image URL and all images
      if (uploadedImages.length > 0) {
        setFormData(prev => ({
          ...prev,
          coverImage: uploadedImages[0].url,
          media: {
            imageUrl: uploadedImages[0].url,
            altText: 'Event image',
            images: uploadedImages.map(img => img.url as string) // Explicitly cast to string[]
          }
        }));
      } else if (images.length === 0 && uploadedImages.length === 0) {
        // If no images are present after upload, clear media data
        setFormData(prev => ({
          ...prev,
          coverImage: '',
          media: {
            imageUrl: '',
      altText: '',
            images: []
          }
        }));
      }

      setUploadProgress(100);
      toast.success('Images uploaded successfully');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload images';
      setError(errorMessage);
      console.error('Upload error:', err);
      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const removeImage = async (index: number) => {
    const imageToRemove = images[index];

    // If the image has a delete URL, try to delete it from ImgBB
    if (imageToRemove?.deleteUrl) {
      try {
        await imageService.deleteImage(imageToRemove.deleteUrl);
      } catch (error: unknown) {
        console.error('Failed to delete image from server:', error);
      }
    }

    setImages(prev => {
      const newImages = prev.filter((_, i) => i !== index);

      if (imageToRemove?.preview && !imageToRemove.preview.startsWith('http')) {
        URL.revokeObjectURL(imageToRemove.preview);
      }

      // Update form data after removal
      setFormData(prev => ({
        ...prev,
        media: {
          ...prev.media,
          images: newImages.map(img => img.url as string).filter(Boolean) as string[],
          imageUrl: newImages[0]?.url || '',
        },
        coverImage: newImages[0]?.url || '',
      }));

      return newImages;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (images.length === 0) {
      setError('Please add at least one image');
      return;
    }

    if (isUploading) {
      setError('Please wait for images to finish uploading');
      return;
    }

    try {
      if (images.length > 0 && images[0]?.url) {
        const mediaData = {
          coverImage: images[0].url,
          media: {
            imageUrl: images[0].url,
            altText: 'Event image',
            images: images.map(img => img.url as string) // Explicitly cast to string[]
          }
        };

        setFormData(prev => ({
          ...prev,
          ...mediaData
        }));

        onNext(mediaData);
      } else {
        setError('Failed to set cover image. Please try uploading again.');
      }
    } catch (error: unknown) {
      console.error('Error in MediaStep submission:', error);
      setError(error instanceof Error ? error.message : 'Failed to create event');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Alert className="mb-6">
        <Info className="h-4 w-4" />
        <AlertTitle>Event Images</AlertTitle>
        <AlertDescription>
          Add high-quality images to showcase your event. The first image will be used as the cover image.
        </AlertDescription>
      </Alert>

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {images.map((image, index) => (
            <div key={index} className="relative group">
              <div className="aspect-video rounded-lg overflow-hidden border">
                <img
                  src={image.preview}
                  alt={`Event image ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Replace broken images with placeholder
                    (e.target as HTMLImageElement).src = 'https://placehold.co/600x400?text=Image+Error';
                  }}
                />
                <button
                        type="button"
                  onClick={() => removeImage(index)}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              {index === 0 && (
                <span className="absolute top-2 left-2 bg-primary text-primary-foreground text-xs px-2 py-1 rounded">
                  Cover Image
                </span>
              )}
                    </div>
                  ))}
          <label className="aspect-video rounded-lg border-2 border-dashed flex items-center justify-center cursor-pointer hover:border-primary transition-colors">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageChange}
              className="hidden"
              disabled={isUploading || isLoading}
            />
            <div className="text-center">
              <ImagePlus className="h-8 w-8 mx-auto text-muted-foreground" />
              <span className="text-sm text-muted-foreground mt-2 block">
                Add Images
              </span>
            </div>
          </label>
        </div>

        {isUploading && (
          <div className="space-y-2">
            <Progress value={uploadProgress} />
            <p className="text-sm text-muted-foreground text-center">
              Uploading images... {Math.round(uploadProgress)}%
            </p>
          </div>
        )}
      </div>

      <Separator className="my-6" />

      <div className="flex justify-between pt-4">
          <Button
            type="button"
            variant="outline"
          onClick={onPrev}
          disabled={isFirst || isUploading || isLoading}
        >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
        <Button 
          type="submit"
          disabled={isUploading || isLoading || images.length === 0}
          className="bg-primary hover:bg-primary/90"
        >
          {isUploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Uploading...
            </>
          ) : isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Creating Event...
            </>
          ) : (
            'Create Event'
          )}
            </Button>
          </div>
        </form>
  );
}

export default CreateEvent;